import i18next, { type i18n as I18nInstance } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

export const supportedLanguages = ["pl", "en", "de"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const languageStorageKey = "roastery.language";

/**
 * One dictionary per chunk, fetched when it is needed (US-37.8, UX-27).
 *
 * <p>The three files are 1.26 MB of JSON — about 375 kB gzipped — and importing all of them at the
 * top of this module put every word of Polish, English and German into the chunk the entry pulls in.
 * The audit read that chunk as "the vendor bundle"; it was mostly translations, and two thirds of
 * them were for languages nobody in that roastery reads. Now a Polish roastery downloads Polish.
 */
const loaders: Record<SupportedLanguage, () => Promise<{ default: Record<string, unknown> }>> = {
  pl: () => import("./locales/pl.json"),
  en: () => import("./locales/en.json"),
  de: () => import("./locales/de.json"),
};

/**
 * The dictionaries a language actually needs — its own, and the ones it falls back through.
 *
 * <p>German falls back through English, so a German screen needs both; without English behind it a
 * key missing from German would show the key instead of a sentence.
 */
function chainFor(language: SupportedLanguage, fallback: SupportedLanguage): SupportedLanguage[] {
  const chain = language === "de" ? ["de", "en", fallback] : [language, fallback];
  return [...new Set(chain)] as SupportedLanguage[];
}

function storedLanguage(): SupportedLanguage | null {
  try {
    const value = window.localStorage.getItem(languageStorageKey);
    return isSupportedLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

async function ensureLoaded(
  instance: I18nInstance,
  language: SupportedLanguage,
  fallback: SupportedLanguage,
): Promise<void> {
  await Promise.all(
    chainFor(language, fallback)
      .filter((needed) => !instance.hasResourceBundle(needed, "translation"))
      .map(async (needed) => {
        const module = await loaders[needed]();
        instance.addResourceBundle(needed, "translation", module.default, true, true);
      }),
  );
}

/**
 * Creates the shared i18next instance.
 *
 * Detection is deliberately limited to the language the user picked here before: the browser's locale
 * is a poor signal for a business application whose users share a roastery, so a first-time visitor
 * gets the configured default and, once signed in, the locale their account carries in Keycloak.
 *
 * The application ships Polish, English and German (US-17.1); `de` is also a supported locale of the
 * Keycloak realm, so the sign-in pages speak the same three.
 *
 * <p>Since US-37.8 this is asynchronous, because the dictionary it starts with is fetched rather than
 * bundled. Callers await it before the first render — an instance that resolves after the tree is
 * mounted would paint raw keys and then replace them.
 */
export async function createI18n(fallbackLanguage: SupportedLanguage = "pl"): Promise<I18nInstance> {
  const language = storedLanguage() ?? fallbackLanguage;
  const loaded = await Promise.all(
    chainFor(language, fallbackLanguage).map(
      async (needed) => [needed, (await loaders[needed]()).default] as const,
    ),
  );
  const resources = Object.fromEntries(loaded.map(([code, translation]) => [code, { translation }]));

  const instance = i18next.createInstance();
  await instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      // German falls back through English rather than straight to the configured default: a reader of
      // German gets further with an English sentence than with a Polish one. The audit in
      // `scripts/check-locales.mjs` is what keeps that path from ever being taken.
      fallbackLng: { de: ["en", fallbackLanguage], default: [fallbackLanguage] },
      supportedLngs: [...supportedLanguages],
      nonExplicitSupportedLngs: true,
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage"],
        lookupLocalStorage: languageStorageKey,
        caches: ["localStorage"],
      },
    });

  // `<html lang>` follows the language actually shown, not the one `index.html` was written in: a
  // screen reader picks its voice from it, and an English screen read with Polish pronunciation is
  // noise. Once now, and on every switch — whichever of the five callers made it.
  syncDocumentLanguage(instance.resolvedLanguage ?? instance.language);
  instance.on("languageChanged", syncDocumentLanguage);

  // Switching language has to fetch before it switches, or every screen shows its keys for as long
  // as the request takes. Wrapping it here keeps that true for all five callers — the two switchers,
  // the profile form and the account locale — without any of them knowing that dictionaries load.
  const change = instance.changeLanguage.bind(instance);
  instance.changeLanguage = (async (next?: string, callback?: Parameters<typeof change>[1]) => {
    if (isSupportedLanguage(next)) {
      await ensureLoaded(instance, next, fallbackLanguage);
    }
    return change(next, callback);
  }) as typeof instance.changeLanguage;

  return instance;
}

function syncDocumentLanguage(language: string | undefined): void {
  // Guarded for a server render, where there is no document to tell.
  if (typeof document === "undefined" || !language) {
    return;
  }
  document.documentElement.lang = language.split("-")[0] ?? language;
}

export function isSupportedLanguage(value: string | undefined | null): value is SupportedLanguage {
  return value !== null && value !== undefined && (supportedLanguages as readonly string[]).includes(value);
}

/** True when this browser has no explicit language choice yet, so an account locale may still apply. */
export function hasStoredLanguageChoice(): boolean {
  try {
    return window.localStorage.getItem(languageStorageKey) !== null;
  } catch {
    // Private mode or blocked storage: treat it as "no choice" and fall back to the account locale.
    return false;
  }
}
